import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { DescriptiveTag } from "@/atoms/tag/DescriptiveTag";
import { get } from "api";
import { Models, OrderPickupStatus } from "api/types";
import { CircleProgressTracker } from "atoms/circle-progress-tracker/CircleProgressTracker";
import { ProgressInfo } from "utils/retailProducts.helpers";

import {
  RetailProductOrderCard,
  RetailProductScanInStoreButton,
} from "../index";

export interface RetailProductPickupSectionProps {
  pickupItems: Models<"DisplayRetailProductOrderLineItemForPickup">[];
  pickupProgress: ProgressInfo;
  locationName?: string;
  orderType: string;
  context: "wallet" | "product-details";
  orderId?: string;
}

export const RetailProductPickupSection = ({
  pickupItems,
  pickupProgress,
  locationName,
  orderType,
  context,
  orderId,
}: RetailProductPickupSectionProps) => {
  const navigate = useNavigate();

  // Get platform location for pickup data
  const { data: platformLocation } = useQuery({
    queryKey: ["/platform-location"],
    queryFn: () => get("/platform-location"),
    staleTime: Infinity,
  });

  if (pickupItems.length === 0) return null;

  const allPickedUp = pickupItems.every(
    (item) => item.status === OrderPickupStatus.PickedUp
  );

  const shouldShowTag = pickupItems.some(
    (item) => item.isFromMembership || item.isFreePackageGroup
  );
  const getTagText = () => {
    const hasMembershipItem = pickupItems.some((item) => item.isFromMembership);
    const hasFreePackageItem = pickupItems.some(
      (item) => item.isFreePackageGroup
    );

    if (hasMembershipItem) {
      return "Membership Benefit";
    } else if (hasFreePackageItem) {
      return "Free Item";
    }
    return "";
  };

  return (
    <div>
      {shouldShowTag && (
        <div className="pb-2">
          <DescriptiveTag color="secondary" size="small">
            {getTagText()}
          </DescriptiveTag>
        </div>
      )}
      <div className="flex flex-row items-center justify-between">
        <div className="mb-3 text-bold3 uppercase">Pickup</div>
        <div className="rounded-md bg-brand-tint px-2 py-1 text-body3">
          <span className="text-bold3">{locationName || "Store"}</span> location
        </div>
      </div>
      <div className="mb-2 space-y-3">
        {pickupItems.map((lineItem) => {
          const displayQuantity =
            lineItem.totalQuantity > 1 && context === "wallet"
              ? lineItem.totalQuantity - lineItem.redeemedQuantity
              : lineItem.totalQuantity;

          return (
            <RetailProductOrderCard
              underline
              context={context}
              key={lineItem.packageId}
              name={lineItem.name.text}
              imageUri={lineItem.thumbnailImageUri || undefined}
              lastRedeemedOnUtc={lineItem.lastRedeemedOnUtc || undefined}
              totalQuantity={lineItem.totalQuantity}
              quantity={displayQuantity}
              onClick={() =>
                navigate(`/pdp/product/${lineItem.packageListingId}`)
              }
            />
          );
        })}
      </div>

      {orderType !== "past" && context !== "wallet" && !allPickedUp && (
        <div className="mb-4 w-full">
          <div className="text-center">
            <p className="text-bold3 uppercase">{pickupProgress.statusText}</p>
          </div>
          <CircleProgressTracker
            current={pickupProgress.current}
            max={pickupProgress.max}
          />
        </div>
      )}
      <RetailProductScanInStoreButton
        disabled={allPickedUp}
        orderId={orderId}
        pickupData={
          !orderId && !allPickedUp && platformLocation
            ? {
                retailProductVariantId: pickupItems[0].packageId,
                productId: pickupItems[0].productId,
                quantity: pickupItems[0].totalQuantity,
                platformLocationId: platformLocation.id,
                isFromMembership: pickupItems[0].isFromMembership,
              }
            : undefined
        }
      />
    </div>
  );
};
